/**
 * Header Countdown Bar Controller
 * Handles scheduled Store Countdown Campaigns from Shopify Metaobjects
 */
(function () {
  'use strict';

  class HeaderCountdown {
    constructor(container) {
      this.container = container;
      this.barElement = container.querySelector('.header-countdown-bar') || (container.matches('[data-product-deal-countdown], [data-product-card-deal-countdown], [data-cart-line-item-deal-countdown]') ? container : container.querySelector('[data-product-deal-countdown], [data-product-card-deal-countdown], [data-cart-line-item-deal-countdown]'));
      if (!this.barElement) return;

      this.messageElement = container.querySelector('[data-countdown-message]');
      this.labelElement = container.querySelector('[data-countdown-label]');
      this.subMessageElement = container.querySelector('[data-countdown-sub-message]');
      this.linkElement = container.querySelector('[data-countdown-link]');
      this.timerElement = container.querySelector('[data-countdown-timer]');
      this.daysEl = container.querySelector('[data-timer-days]');
      this.hoursEl = container.querySelector('[data-timer-hours]');
      this.daysUnitEl = container.querySelector('[data-timer-days-unit]');
      this.hoursUnitEl = container.querySelector('[data-timer-hours-unit]');
      this.minsEl = container.querySelector('[data-timer-minutes]');
      this.secsEl = container.querySelector('[data-timer-seconds]');
      this.progressEl = container.querySelector('[data-countdown-progress]');
      const mainProductInfo = this.barElement.closest('.main-product-info');
      const productCard = this.barElement.closest('li.product-card');
      const priceContainerParent = mainProductInfo || productCard;

      this.priceContainers = priceContainerParent
        ? priceContainerParent.querySelectorAll('.timer-price')
        : [];

      const jsonScript = container.querySelector('[data-countdown-json]');
      this.campaigns = [];
      if (jsonScript && jsonScript.textContent) {
        try {
          this.campaigns = JSON.parse(jsonScript.textContent);
        } catch (e) {
          console.error('[HeaderCountdown] Invalid campaigns JSON', e);
        }
      }

      this.timerId = null;
      this.activeCampaign = null;

      this.init();
    }

    init() {
      if (!Array.isArray(this.campaigns) || this.campaigns.length === 0) {
        this.hideBar();
        return;
      }

      this.evaluateAndRender();
      this.startTimer();
    }

    evaluateActiveCampaign() {
      const now = Date.now();
      const activeList = [];

      for (let i = 0; i < this.campaigns.length; i++) {
        const c = this.campaigns[i];
        if (!c || !c.start_date || !c.end_date) continue;

        const startTime = new Date(c.start_date).getTime();
        const endTime = new Date(c.end_date).getTime();

        if (isNaN(startTime) || isNaN(endTime)) continue;

        if (startTime <= now && now < endTime) {
          activeList.push({
            ...c,
            startTime,
            endTime
          });
        }
      }

      if (activeList.length === 0) {
        return null;
      }

      // Sort by latest start_date (descending).
      // Deterministic fallback if start dates are identical: sort by ID / title.
      activeList.sort((a, b) => {
        if (b.startTime !== a.startTime) {
          return b.startTime - a.startTime;
        }
        const idA = String(a.id || a.title || '');
        const idB = String(b.id || b.title || '');
        return idB.localeCompare(idA);
      });

      return activeList[0];
    }

    evaluateAndRender() {
      const campaign = this.evaluateActiveCampaign();

      if (!campaign) {
        this.activeCampaign = null;
        this.hideBar();
        return false;
      }

      this.activeCampaign = campaign;
      this.renderCampaignData(campaign);
      this.showBar();
      this.updateCountdown();
      return true;
    }

    renderCampaignData(campaign) {
      if (this.messageElement) {
        this.messageElement.textContent = campaign.message || '';
      }

      if (this.labelElement) {
        this.labelElement.textContent = campaign.label || campaign.title || '';
      }

      if (this.subMessageElement) {
        this.subMessageElement.textContent = campaign.sub_message || '';
      }

      if (this.linkElement) {
        if (campaign.link) {
          this.linkElement.href = campaign.link;
        } else {
          this.linkElement.removeAttribute('href');
        }
      }
    }

    updateCountdown() {
      if (!this.activeCampaign) return;

      const now = Date.now();
      const remainingMs = this.activeCampaign.endTime - now;

      if (remainingMs <= 0) {
        // Current campaign expired. Re-evaluate available campaigns immediately.
        const hasNext = this.evaluateAndRender();
        if (!hasNext) {
          this.stopTimer();
        }
        return;
      }

      const totalSecs = Math.floor(remainingMs / 1000);
      const days = Math.floor(totalSecs / 86400);
      const hours = Math.floor((totalSecs % 86400) / 3600);
      const minutes = Math.floor((totalSecs % 3600) / 60);
      const seconds = totalSecs % 60;

      const formatNum = (num) => String(num).padStart(2, '0');

      if (this.daysEl) this.daysEl.textContent = String(days);
      if (this.hoursEl) this.hoursEl.textContent = formatNum(hours);
      if (this.daysUnitEl) this.daysUnitEl.textContent = `${this.daysUnitEl.dataset.timerUnitPrefix || ''}${days === 1 ? 'Day' : 'Days'}`;
      if (this.hoursUnitEl) this.hoursUnitEl.textContent = `${this.hoursUnitEl.dataset.timerUnitPrefix || ''}${hours === 1 ? 'Hr' : 'Hrs'}`;
      if (this.minsEl) this.minsEl.textContent = formatNum(minutes);
      if (this.secsEl) this.secsEl.textContent = formatNum(seconds);

      if (this.barElement) {
        this.barElement.classList.toggle('is-ending-soon', remainingMs <= 3600000);
      }

      if (this.progressEl) {
        const durationMs = this.activeCampaign.endTime - this.activeCampaign.startTime;
        const progress = durationMs > 0 ? Math.max(0, Math.min(100, (remainingMs / durationMs) * 100)) : 0;
        this.progressEl.style.width = `${progress}%`;
      }
    }

    startTimer() {
      this.stopTimer();
      if (!this.activeCampaign) return;

      this.timerId = setInterval(() => {
        this.updateCountdown();
      }, 1000);
    }

    stopTimer() {
      if (this.timerId) {
        clearInterval(this.timerId);
        this.timerId = null;
      }
    }

    showBar() {
      if (this.barElement) {
        this.barElement.hidden = false;
        this.barElement.classList.remove('is-hidden');
        this.priceContainers.forEach((priceContainer)=>{
          priceContainer.classList.remove('is-hidden');
        });
      }
    }

    hideBar() {
      this.stopTimer();
      if (this.barElement) {
        this.barElement.hidden = true;
        this.barElement.classList.add('is-hidden');
        this.priceContainers.forEach((priceContainer)=>{
          priceContainer.classList.add('is-hidden');
        });
      }
    }

    destroy() {
      this.stopTimer();
    }
  }

  function initHeaderCountdownSections() {
    const containers = document.querySelectorAll('[data-section-type="header-countdown"], [data-section-type="product-deal-countdown"], [data-section-type="product-card-deal-countdown"], [data-section-type="cart-line-item-deal-countdown"]');
    containers.forEach((container) => {
      if (!container._headerCountdown) {
        container._headerCountdown = new HeaderCountdown(container);
      }
    });
  }

  function initCountdownsInNode(node) {
    if (!node || node.nodeType !== 1) return;

    if (node.matches && node.matches('[data-section-type="header-countdown"], [data-section-type="product-deal-countdown"], [data-section-type="product-card-deal-countdown"], [data-section-type="cart-line-item-deal-countdown"]')) {
      if (!node._headerCountdown) {
        node._headerCountdown = new HeaderCountdown(node);
      }
    }

    if (node.querySelectorAll) {
      node.querySelectorAll('[data-section-type="header-countdown"], [data-section-type="product-deal-countdown"], [data-section-type="product-card-deal-countdown"], [data-section-type="cart-line-item-deal-countdown"]').forEach((container) => {
        if (!container._headerCountdown) {
          container._headerCountdown = new HeaderCountdown(container);
        }
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initHeaderCountdownSections);
  } else {
    initHeaderCountdownSections();
  }

  if ('MutationObserver' in window) {
    let observerFrame = null;
    const pendingNodes = new Set();
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => pendingNodes.add(node));
      });

      if (observerFrame) return;

      observerFrame = window.requestAnimationFrame(() => {
        pendingNodes.forEach(initCountdownsInNode);
        pendingNodes.clear();
        observerFrame = null;
      });
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true
    });
  }

  // Shopify Theme Editor listener support
  document.addEventListener('shopify:section:load', function (event) {
    if (!event.target) return;

    event.target.querySelectorAll('[data-section-type="header-countdown"], [data-section-type="product-deal-countdown"], [data-section-type="product-card-deal-countdown"], [data-section-type="cart-line-item-deal-countdown"]').forEach((container) => {
      if (container._headerCountdown) {
        container._headerCountdown.destroy();
      }
      container._headerCountdown = new HeaderCountdown(container);
    });
  });

  document.addEventListener('shopify:section:unload', function (event) {
    if (!event.target) return;

    event.target.querySelectorAll('[data-section-type="header-countdown"], [data-section-type="product-deal-countdown"], [data-section-type="product-card-deal-countdown"], [data-section-type="cart-line-item-deal-countdown"]').forEach((container) => {
      if (container._headerCountdown) {
        container._headerCountdown.destroy();
        delete container._headerCountdown;
      }
    });
  });
})();